import {
  AlertTriangle,
  ExternalLink,
  Info,
  MoreHorizontal,
} from "lucide-react";
import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { SuggestionChips } from "../ui/suggestion-chips";
import { ServiceInfoModal } from "./ServiceInfoModal";
import type {
  IdentifiedService,
  LookupService,
} from "./step-2-service-mapping";

import type { SelectedService } from "@/types/service";

interface ServiceTabsProps {
  services: IdentifiedService[];
  selections: SelectedService[];
  activeService: IdentifiedService | null;
  hoveredCaen: string;
  selectedListaDetails: IdentifiedService["lista_details"][0] | null;
  selectedSmartSearch: boolean;
  caenDescriptions: Record<string, { title: string; description: string }>;
  onServiceToggle: (service: IdentifiedService) => void;
  onServiceClick: (service: IdentifiedService) => void;
  onCaenHover: (caen: string) => void;
  onListaDetailsClick: (
    details: IdentifiedService["lista_details"][0] | null
  ) => void;
  onSmartSearchClick: (selected: boolean) => void;
}

export function ServiceTabs({
  services,
  selections,
  activeService,
  hoveredCaen,
  selectedListaDetails,
  selectedSmartSearch,
  caenDescriptions,
  onServiceToggle,
  onServiceClick,
  onCaenHover,
  onListaDetailsClick,
  onSmartSearchClick,
}: ServiceTabsProps) {
  const isMobile = useIsMobile();
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
  const [currentColumn, setCurrentColumn] = React.useState(0);

  // Service info modal state
  const [serviceInfoModalOpen, setServiceInfoModalOpen] = useState(false);
  const [selectedServiceInfo, setSelectedServiceInfo] =
    useState<LookupService | null>(null);

  React.useEffect(() => {
    if (!carouselApi) return;

    carouselApi.on("select", () => {
      setCurrentColumn(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);
  // Get detailed CAEN info for hovered CAEN using real data
  const getCaenDetails = (caenCode: string) => {
    const caenInfo = caenDescriptions[caenCode];
    if (caenInfo) {
      return {
        code: caenCode,
        title: caenInfo.title,
        description: caenInfo.description,
      };
    }

    return {
      code: caenCode,
      title: `Codul CAEN ${caenCode}`,
      description: "Descrierea detaliată nu este disponibilă.",
    };
  };

  if (services.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Nu există servicii pentru această anexă.</p>
      </div>
    );
  }

  // Enhanced handlers for mobile navigation
  const handleServiceClick = (service: IdentifiedService) => {
    onServiceClick(service);
    // Auto-scroll to CAEN codes column on mobile
    if (isMobile && carouselApi) {
      carouselApi.scrollTo(1);
    }
  };

  const handleCaenClick = (caenCode: string) => {
    onCaenHover(hoveredCaen === caenCode ? "" : caenCode);
    // Clear lista details when selecting a CAEN
    if (selectedListaDetails) {
      onListaDetailsClick(null);
    }
    // Auto-scroll to details column on mobile
    if (isMobile && carouselApi) {
      carouselApi.scrollTo(2);
    }
  };

  const handleListaDetailsClick = (
    details: IdentifiedService["lista_details"][0] | null
  ) => {
    onListaDetailsClick(selectedListaDetails === details ? null : details);
    // Clear CAEN selection when selecting lista details
    if (hoveredCaen) {
      onCaenHover("");
    }
    // Auto-scroll to details column on mobile
    if (isMobile && carouselApi) {
      carouselApi.scrollTo(2);
    }
  };

  const handleSmartSearchClick = (selected: boolean) => {
    onSmartSearchClick(!selectedSmartSearch);
    // Clear other selections when selecting smart search
    if (selectedListaDetails) {
      onListaDetailsClick(null);
    }
    if (hoveredCaen) {
      onCaenHover("");
    }
    // Auto-scroll to details column on mobile
    if (isMobile && carouselApi) {
      carouselApi.scrollTo(2);
    }
  };

  if (isMobile) {
    return (
      <div className="space-y-4">
        <Carousel
          setApi={setCarouselApi}
          opts={{
            align: "start",
            loop: false,
            dragFree: false,
            containScroll: "trimSnaps",
          }}
        >
          <CarouselContent>
            {/* Column 1: Services */}
            <CarouselItem className="max-w-[calc(100%-48px)]">
              <Card className="h-[320px]">
                <CardContent className="p-3">
                  <h3 className="font-semibold mb-2 text-xs text-muted-foreground uppercase tracking-wide">
                    Servicii
                  </h3>
                  <ScrollArea className="h-[280px]">
                    <div className="space-y-2 pr-3">
                      {services.map((service) => {
                        const isSelected = selections.some(
                          (s) => s.entity_id === service.entity_id
                        );
                        const isActive = activeService?.id === service.id;

                        return (
                          <div
                            key={service.id}
                            className={`p-2 rounded-lg border cursor-pointer transition-all ${
                              isActive
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/50 hover:bg-muted/50"
                            }`}
                            onClick={() => handleServiceClick(service)}
                          >
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center flex-1 gap-2">
                                  <span className="font-medium text-xs text-muted-foreground">
                                    {service.sector_name}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0 hover:bg-transparent"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedServiceInfo(service);
                                      setServiceInfoModalOpen(true);
                                    }}
                                  >
                                    <Info className="h-3 w-3 text-primary" />
                                  </Button>
                                  {service.scor_incredere !== null &&
                                    service.scor_incredere !== undefined && (
                                      <TooltipProvider delayDuration={200}>
                                        <Tooltip>
                                          <TooltipTrigger className="ml-auto">
                                            <div
                                              className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                                service.scor_incredere < 50
                                                  ? "bg-ds-red-500/10 border border-ds-red-500/30"
                                                  : service.scor_incredere <= 75
                                                    ? "bg-ds-blue-100 border border-ds-blue-300"
                                                    : "bg-ds-green-100 border border-ds-green-500/30"
                                              }`}
                                            >
                                              <span
                                                className={`text-xs font-medium ${
                                                  service.scor_incredere < 50
                                                    ? "text-ds-red-500"
                                                    : service.scor_incredere <=
                                                        75
                                                      ? "text-ds-blue-700"
                                                      : "text-ds-green-600"
                                                }`}
                                              >
                                                {service.scor_incredere}
                                              </span>
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-xs">
                                            <p>
                                              {service.scor_descriere ||
                                                "Scor de încredere pentru identificarea serviciului"}
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                </div>
                                <Switch
                                  checked={isSelected}
                                  onCheckedChange={() =>
                                    onServiceToggle(service)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex-shrink-0"
                                />
                              </div>
                              <h4 className="font-semibold text-xs text-primary">
                                {service.name}
                              </h4>
                              <p className="text-xs text-muted-foreground line-clamp-2 leading-tight">
                                {service.subsector_name}
                              </p>
                              <div className="mt-1">
                                <SuggestionChips service={service} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </CarouselItem>

            {/* Column 2: CAEN Codes, Lista Oficiala, Smart Search */}
            <CarouselItem className="max-w-[calc(100%-48px)]">
              <Card className="h-[320px]">
                <CardContent className="p-3">
                  <ScrollArea className="h-[292px]">
                    <div className="space-y-3 pr-3">
                      {/* Section 1: CAEN Codes */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
                            {activeService
                              ? "Coduri CAEN pentru serviciu"
                              : "Toate codurile CAEN"}
                          </h3>
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="w-3 h-3 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>
                                  Mapăm codurile CAEN cu serviciile NIS2 pe baza
                                  sectoarelor, subsectoarelor și serviciilor din
                                  directivă, împreună cu clasificarea CAEN V2 &
                                  V3
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        {(() => {
                          let caenCodes = Array.from(
                            new Set(
                              services.flatMap(
                                (service) =>
                                  service[
                                    `CAEN_rev_${service.caen_version || 3}`
                                  ]
                              )
                            )
                          ).sort();
                          if (activeService) {
                            caenCodes =
                              activeService[
                                `CAEN_rev_${activeService.caen_version || 3}`
                              ] || [];
                            if (
                              activeService.is_super_ambiguu &&
                              activeService.super_ambiguu?.byCaen_secundar
                            ) {
                              caenCodes =
                                activeService[
                                  `caen_rev_${activeService.caen_version || 3}_secundare`
                                ] || [];
                            }
                            if (
                              activeService.is_super_ambiguu &&
                              activeService.super_ambiguu?.byCaen_secundar &&
                              activeService.super_ambiguu?.byCaen_principal
                            ) {
                              caenCodes = [
                                ...activeService[
                                  `caen_rev_${activeService.caen_version || 3}_secundare`
                                ],
                                ...activeService[
                                  `CAEN_rev_${activeService.caen_version || 3}`
                                ],
                              ];
                            }
                          }
                          return caenCodes.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {caenCodes.map((caenCode) => (
                                <Badge
                                  key={caenCode}
                                  variant="outline"
                                  className={`cursor-pointer transition-colors ${
                                    hoveredCaen === caenCode
                                      ? "bg-primary text-primary-foreground border-primary"
                                      : "hover:bg-muted hover:border-primary/50"
                                  }`}
                                  onClick={() => handleCaenClick(caenCode)}
                                >
                                  CAEN {caenCode}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted-foreground">
                              <p className="text-sm">
                                {activeService
                                  ? "Nu există coduri CAEN pentru acest serviciu."
                                  : "Nu există coduri CAEN pentru serviciile din această anexă."}
                              </p>
                            </div>
                          );
                        })()}
                      </div>

                      <Separator />

                      {/* Section 2: Lista Oficiala */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
                            Lista oficiala
                          </h3>
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="w-3 h-3 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>
                                  Verificări în bazele de date oficiale publice
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        {(() => {
                          // Check if active service has lista_oficiala = true
                          const hasListaOficiala =
                            activeService &&
                            "lista_oficiala" in activeService &&
                            activeService.lista_oficiala;

                          if (hasListaOficiala) {
                            const listaDetailsArray =
                              activeService.lista_details;
                            return (
                              <div className="space-y-2">
                                {listaDetailsArray &&
                                listaDetailsArray.length > 0 ? (
                                  listaDetailsArray.map(
                                    (listaDetails, index) => (
                                      <div key={index} className="space-y-1">
                                        {listaDetails.url_name && (
                                          <Badge
                                            variant="default"
                                            className={`cursor-pointer transition-colors ${
                                              selectedListaDetails ===
                                              listaDetails
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-ds-green-100 text-ds-green-600 border-ds-green-500/30 hover:bg-ds-green-100"
                                            }`}
                                            onClick={() =>
                                              handleListaDetailsClick(
                                                listaDetails
                                              )
                                            }
                                          >
                                            {listaDetails.url_name}
                                          </Badge>
                                        )}
                                      </div>
                                    )
                                  )
                                ) : (
                                  <p className="text-sm text-muted-foreground">
                                    Găsit în liste oficiale (fără detalii)
                                  </p>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div className="text-center py-4 text-muted-foreground">
                              <p className="text-sm">
                                {activeService
                                  ? "Nu am gasit informatii despre acest serviciu pe listele oficiale publice"
                                  : "Nu am gasit informatii despre compania ta pe listele oficiale publice"}
                              </p>
                            </div>
                          );
                        })()}
                      </div>

                      <Separator />

                      {/* Section 3: Smart Search */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
                            Smart search
                          </h3>
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="w-3 h-3 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>
                                  Căutare inteligentă cu AI în surse publice
                                  online
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        {(() => {
                          // Check if active service has smart_search = true
                          const hasSmartSearch =
                            activeService &&
                            "smart_search" in activeService &&
                            activeService.smart_search;

                          if (hasSmartSearch) {
                            const smartSearchDetails =
                              activeService.lista_details;

                            return (
                              <div className="space-y-2">
                                {smartSearchDetails &&
                                smartSearchDetails.length > 0 ? (
                                  smartSearchDetails.map(
                                    (smartDetail, index) => (
                                      <div key={index} className="space-y-1">
                                        {smartDetail.url_name && (
                                          <Badge
                                            variant="default"
                                            className={`cursor-pointer transition-colors ${
                                              selectedSmartSearch
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-ds-blue-100 text-ds-blue-800 border-ds-blue-300 hover:bg-ds-blue-200"
                                            }`}
                                            onClick={() =>
                                              handleSmartSearchClick(
                                                !selectedSmartSearch
                                              )
                                            }
                                          >
                                            {smartDetail.url_name}
                                          </Badge>
                                        )}
                                      </div>
                                    )
                                  )
                                ) : (
                                  <div className="space-y-1">
                                    <Badge
                                      variant="default"
                                      className={`cursor-pointer transition-colors ${
                                        selectedSmartSearch
                                          ? "bg-primary text-primary-foreground border-primary"
                                          : "bg-ds-blue-100 text-ds-blue-800 border-ds-blue-300 hover:bg-ds-blue-200"
                                      }`}
                                      onClick={() =>
                                        handleSmartSearchClick(
                                          !selectedSmartSearch
                                        )
                                      }
                                    >
                                      Smart Search Rezultat
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div className="text-center py-4 text-muted-foreground">
                              <p className="text-sm">
                                {activeService
                                  ? "Nu am gasit informatii despre acest serviciu prin căutare inteligentă"
                                  : "Selectați un serviciu pentru a vedea rezultatele căutării inteligente"}
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </CarouselItem>

            {/* Column 3: Details */}
            <CarouselItem className="max-w-[calc(100%-48px)]">
              <Card className="h-[320px]">
                <CardContent className="p-3">
                  <h3 className="font-semibold mb-2 text-xs text-muted-foreground uppercase tracking-wide">
                    Detalii
                  </h3>
                  <ScrollArea className="h-[280px]">
                    <div className="space-y-3 pr-3">
                      {/* Display details based on selection */}
                      {selectedListaDetails ? (
                        <div>
                          <h4 className="font-semibold mb-2 text-xs text-primary">
                            {selectedListaDetails.url_name}
                          </h4>
                          <p className="text-xs text-foreground mb-3">
                            {selectedListaDetails.descriere}
                          </p>
                          {selectedListaDetails.url && (
                            <a
                              href={selectedListaDetails.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-primary hover:underline text-xs"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Vezi sursa oficiala
                            </a>
                          )}
                          <Badge
                            variant="outline"
                            className="mt-2 bg-ds-green-100 text-xs"
                          >
                            Verificat în liste oficiale
                          </Badge>
                        </div>
                      ) : selectedSmartSearch && activeService ? (
                        <div>
                          <h4 className="font-semibold mb-2 text-xs text-primary">
                            Rezultat Smart Search
                          </h4>
                          <p className="text-xs text-foreground mb-3">
                            Acest serviciu a fost identificat prin căutare
                            inteligentă cu AI în surse publice online pentru
                            compania {activeService.name}.
                          </p>
                          <Badge
                            variant="outline"
                            className="mt-2 bg-ds-blue-50 text-xs"
                          >
                            Identificat prin AI
                          </Badge>
                        </div>
                      ) : hoveredCaen ? (
                        (() => {
                          const caenDetails = getCaenDetails(hoveredCaen);
                          return (
                            <div>
                              <h4 className="font-semibold mb-2 text-primary">
                                {caenDetails.title}
                              </h4>
                              <p className="text-sm text-foreground mb-4">
                                {caenDetails.description}
                              </p>
                              <Badge variant="outline" className="bg-muted">
                                Cod CAEN {caenDetails.code}
                              </Badge>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <p className="text-sm">
                            Selectați un cod CAEN, o intrare din lista oficială
                            sau din smart search pentru a vedea detaliile.
                          </p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </CarouselItem>
          </CarouselContent>
        </Carousel>
        {/* Service Info Modal */}
        <ServiceInfoModal
          open={serviceInfoModalOpen}
          onOpenChange={setServiceInfoModalOpen}
          service={selectedServiceInfo}
        />

        {/* Mobile Navigation Indicators */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex justify-center gap-2">
            {[0, 1, 2].map((index) => (
              <button
                key={index}
                onClick={() => carouselApi?.scrollTo(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  currentColumn === index ? "bg-primary" : "bg-muted"
                }`}
                aria-label={`Go to column ${index + 1}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <MoreHorizontal className="w-4 h-4 animate-pulse" />
            <span>
              Glisați pentru a naviga între coloane ({currentColumn + 1}/3)
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout (original)
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[400px]">
      {/* Column 1: Services */}
      <Card>
        <CardContent className="p-4 md:p-4">
          <h3 className="font-heading font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wide">
            Servicii
          </h3>
          <div className="space-y-3 max-h-[340px] overflow-y-auto">
            {services.map((service) => {
              const isSelected = selections.some(
                (s) => s.entity_id === service.entity_id
              );
              const isActive = activeService?.id === service.id;

              return (
                <div
                  key={service.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    isActive
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50 hover:bg-muted/50"
                  }`}
                  onClick={() => onServiceClick(service)}
                >
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center flex-1 gap-2">
                        <span className="font-medium text-sm text-muted-foreground">
                          {service.sector_name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-transparent"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedServiceInfo(service);
                            setServiceInfoModalOpen(true);
                          }}
                        >
                          <Info className="h-3 w-3 text-primary" />
                        </Button>
                        {service.scor_incredere !== null &&
                          service.scor_incredere !== undefined && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger className="ml-auto">
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                      service.scor_incredere < 50
                                        ? "bg-ds-red-500/10 border border-ds-red-500/30"
                                        : service.scor_incredere <= 75
                                          ? "bg-ds-blue-100 border border-ds-blue-300"
                                          : "bg-ds-green-100 border border-ds-green-500/30"
                                    }`}
                                  >
                                    <span
                                      className={`text-xs font-medium ${
                                        service.scor_incredere < 50
                                          ? "text-ds-red-500"
                                          : service.scor_incredere <= 75
                                            ? "text-ds-blue-700"
                                            : "text-ds-green-600"
                                      }`}
                                    >
                                      {service.scor_incredere}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>
                                    {service.scor_descriere ||
                                      "Scor de încredere pentru identificarea serviciului"}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                      </div>
                      <Switch
                        checked={isSelected}
                        onCheckedChange={() => onServiceToggle(service)}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0"
                      />
                    </div>
                    <h4 className="font-semibold text-sm text-primary">
                      {service.name}
                    </h4>
                    <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                      {service.subsector_name}
                    </p>
                    <div className="mt-2">
                      <SuggestionChips service={service} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Column 2: Three Sections */}
      <Card>
        <CardContent className="p-4 md:p-4">
          <div className="max-h-[372px] overflow-y-auto space-y-4">
            {/* Section 1: CAEN Codes */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  {activeService
                    ? "Coduri CAEN pentru serviciu"
                    : "Toate codurile CAEN"}
                </h3>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>
                        Mapăm codurile CAEN cu serviciile NIS2 pe baza
                        sectoarelor, subsectoarelor și serviciilor din
                        directivă, împreună cu clasificarea CAEN V2 & V3
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {(() => {
                // Get CAEN codes for the active service, or all services if none selected
                let caenCodes = Array.from(
                  new Set(
                    services.flatMap(
                      (service) =>
                        service[`CAEN_rev_${service.caen_version || 3}`]
                    )
                  )
                ).sort();
                if (activeService) {
                  caenCodes =
                    activeService[
                      `CAEN_rev_${activeService.caen_version || 3}`
                    ] || [];
                  if (
                    activeService.is_super_ambiguu &&
                    activeService.super_ambiguu?.byCaen_secundar
                  ) {
                    caenCodes =
                      activeService[
                        `caen_rev_${activeService.caen_version || 3}_secundare`
                      ] || [];
                  }
                  if (
                    activeService.is_super_ambiguu &&
                    activeService.super_ambiguu?.byCaen_secundar &&
                    activeService.super_ambiguu?.byCaen_principal
                  ) {
                    caenCodes = [
                      ...activeService[
                        `caen_rev_${activeService.caen_version || 3}_secundare`
                      ],
                      ...activeService[
                        `CAEN_rev_${activeService.caen_version || 3}`
                      ],
                    ];
                  }
                }

                return caenCodes.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {caenCodes.map((caenCode) => (
                      <Badge
                        key={caenCode}
                        variant="outline"
                        className={`cursor-pointer transition-colors ${
                          hoveredCaen === caenCode
                            ? "bg-primary text-primary-foreground border-primary"
                            : "hover:bg-muted hover:border-primary/50"
                        }`}
                        onClick={() => {
                          onCaenHover(hoveredCaen === caenCode ? "" : caenCode);
                          // Clear lista details when selecting a CAEN
                          if (selectedListaDetails) {
                            onListaDetailsClick(null);
                          }
                        }}
                      >
                        CAEN {caenCode}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <p className="text-sm">
                      {activeService
                        ? "Nu există coduri CAEN pentru acest serviciu."
                        : "Nu există coduri CAEN pentru serviciile din această anexă."}
                    </p>
                  </div>
                );
              })()}
            </div>

            <Separator />

            {/* Section 2: Lista Oficiala */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Lista oficiala
                </h3>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Verificări în bazele de date oficiale publice</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {(() => {
                // Check if active service has lista_oficiala = true
                const hasListaOficiala =
                  activeService &&
                  "lista_oficiala" in activeService &&
                  activeService.lista_oficiala;
                console.log(
                  "🚀 ~ hasListaOficiala:",
                  hasListaOficiala,
                  activeService
                );

                if (hasListaOficiala) {
                  const listaDetailsArray = activeService.lista_details;
                  return (
                    <div className="space-y-2">
                      {listaDetailsArray && listaDetailsArray.length > 0 ? (
                        listaDetailsArray.map((listaDetails, index) => (
                          <div key={index} className="space-y-1">
                            {listaDetails.url_name && (
                              <Badge
                                variant="default"
                                className={`cursor-pointer transition-colors ${
                                  selectedListaDetails === listaDetails
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-ds-green-100 text-ds-green-600 border-ds-green-500/30 hover:bg-ds-green-100"
                                }`}
                                onClick={() => {
                                  onListaDetailsClick(
                                    selectedListaDetails === listaDetails
                                      ? null
                                      : listaDetails
                                  );
                                  // Clear CAEN selection when selecting lista details
                                  if (hoveredCaen) {
                                    onCaenHover("");
                                  }
                                }}
                              >
                                {listaDetails.url_name}
                              </Badge>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Găsit în liste oficiale (fără detalii)
                        </p>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="text-center py-4 text-muted-foreground">
                    <p className="text-sm">
                      {activeService
                        ? "Nu am gasit informatii despre acest serviciu pe listele oficiale publice"
                        : "Nu am gasit informatii despre compania ta pe listele oficiale publice"}
                    </p>
                  </div>
                );
              })()}
            </div>

            <Separator />

            {/* Section 3: Smart Search */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Smart search
                </h3>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Căutare inteligentă cu AI în surse publice online</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {(() => {
                // Check if active service has smart_search = true
                const hasSmartSearch =
                  activeService &&
                  "smart_search" in activeService &&
                  activeService.smart_search;

                if (hasSmartSearch) {
                  const smartSearchDetails = activeService.lista_details;

                  return (
                    <div className="space-y-2">
                      {smartSearchDetails && smartSearchDetails.length > 0 ? (
                        smartSearchDetails.map((smartDetail, index) => (
                          <div key={index} className="space-y-1">
                            {smartDetail.url_name && (
                              <Badge
                                variant="default"
                                className={`cursor-pointer transition-colors ${
                                  selectedSmartSearch
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-ds-blue-100 text-ds-blue-800 border-ds-blue-300 hover:bg-ds-blue-200"
                                }`}
                                onClick={() => {
                                  onSmartSearchClick(!selectedSmartSearch);
                                  // Clear other selections when selecting smart search
                                  if (selectedListaDetails) {
                                    onListaDetailsClick(null);
                                  }
                                  if (hoveredCaen) {
                                    onCaenHover("");
                                  }
                                }}
                              >
                                {smartDetail.url_name}
                              </Badge>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="space-y-1">
                          <Badge
                            variant="default"
                            className={`cursor-pointer transition-colors ${
                              selectedSmartSearch
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-ds-blue-100 text-ds-blue-800 border-ds-blue-300 hover:bg-ds-blue-200"
                            }`}
                            onClick={() => {
                              onSmartSearchClick(!selectedSmartSearch);
                              // Clear other selections when selecting smart search
                              if (selectedListaDetails) {
                                onListaDetailsClick(null);
                              }
                              if (hoveredCaen) {
                                onCaenHover("");
                              }
                            }}
                          >
                            Smart Search
                          </Badge>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="text-center py-4 text-muted-foreground">
                    <p className="text-sm">
                      {activeService
                        ? "Nu am gasit informatii despre acest serviciu prin căutare inteligentă"
                        : "Nu am gasit informatii despre compania ta pe din surse publice online si nici cu algoritmul nostru AI"}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Column 3: Details */}
      <Card>
        <CardContent className="p-4 md:p-4">
          <h3 className="font-heading font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wide">
            {selectedListaDetails
              ? "Detalii Lista Oficială"
              : selectedSmartSearch
                ? "Detalii Smart Search"
                : hoveredCaen
                  ? "Detalii CAEN"
                  : "Detalii servicii"}
          </h3>
          <div className="max-h-[340px] overflow-y-auto">
            {selectedSmartSearch && activeService ? (
              <div className="space-y-3">
                {activeService.lista_details &&
                activeService.lista_details.length > 0 ? (
                  activeService.lista_details.map((smartDetail, index) => (
                    <div key={index} className="space-y-3">
                      <div>
                        <Badge
                          variant="default"
                          className="mb-2 bg-ds-blue-100 text-ds-blue-800 border-ds-blue-300"
                        >
                          {smartDetail.url_name}
                        </Badge>
                      </div>
                      <div>
                        <h5 className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-1">
                          Descriere
                        </h5>
                        <p className="text-sm text-foreground leading-relaxed">
                          {smartDetail.descriere}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="space-y-3">
                    <div>
                      <Badge
                        variant="default"
                        className="mb-2 bg-ds-blue-100 text-ds-blue-800 border-ds-blue-300"
                      >
                        Smart Search
                      </Badge>
                    </div>
                    <div>
                      <h5 className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-1">
                        Descriere
                      </h5>
                      <p className="text-sm text-foreground leading-relaxed">
                        Acest serviciu a fost identificat prin căutare
                        inteligentă cu AI în surse publice online.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : selectedListaDetails ? (
              <div className="space-y-3">
                <div>
                  <Badge
                    variant="default"
                    className="mb-2 bg-ds-green-100 text-ds-green-600 border-ds-green-500/30"
                  >
                    {selectedListaDetails.url_name}
                  </Badge>
                </div>
                <div>
                  <h5 className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-2">
                    Link
                  </h5>
                  <a
                    href={selectedListaDetails.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-ds-blue-600 hover:text-ds-blue-800 underline break-all flex items-center gap-1"
                  >
                    <span>{selectedListaDetails.url}</span>
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                </div>
                <div>
                  <h5 className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Descriere
                  </h5>
                  <p className="text-sm text-foreground leading-relaxed">
                    {selectedListaDetails.descriere}
                  </p>
                </div>
              </div>
            ) : hoveredCaen ? (
              <div className="space-y-3">
                {(() => {
                  const details = getCaenDetails(hoveredCaen);
                  return (
                    <>
                      <div>
                        <Badge variant="default" className="mb-2">
                          CAEN {details.code}
                        </Badge>
                        <h4 className="font-semibold text-sm text-primary mb-2">
                          {details.title}
                        </h4>
                      </div>
                      <div>
                        <h5 className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-1">
                          Descriere
                        </h5>
                        <p className="text-sm text-foreground leading-relaxed">
                          {details.description}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">
                  Click pe un cod CAEN, sursă oficială sau Smart Search pentru
                  detalii.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Service Info Modal */}
      <ServiceInfoModal
        open={serviceInfoModalOpen}
        onOpenChange={setServiceInfoModalOpen}
        service={selectedServiceInfo}
      />
    </div>
  );
}
